import { EventMeta } from '../../interfaces/meta.interface';

// LogonError types observed:

// UserStrongAuthEnrollmentRequiredInterrupt
// UserStrongAuthClientAuthNRequiredInterrupt
// UserAccountNotFound
// InvalidUserNameOrPassword
// SsoArtifactInvalidOrExpired
// KmsiInterrupt
// DelegationDoesNotExist
// None
//
// Also seen on staging UserLoginFailed events, September to October 2026:
// CmsiInterrupt
// UserDeclinedConsent
// DeviceIsDisabled
// FidoBlockedByPolicy
// AdminConsentRequiredRequestAccess
type Fields = any;

/** Whether a `LogonError` names a real error: Entra writes 'None' (or nothing) on a clean sign-in. */
const isRealLogonError = (value: unknown): value is string =>
    typeof value === 'string' && value.trim() !== '' && value.trim().toLowerCase() !== 'none';

/**
 * Whether an STS logon record is an interruption, not a completed sign-in.
 *
 * Microsoft is explicit that `ResultStatus` cannot tell: for STS logons 'Succeeded' means only that the HTTP
 * operation succeeded, and the logon itself is judged by `LogonError`; `ErrorNumber` (documented as `ErrorCode`)
 * carries the AADSTS code, 0 for a successful logon. Entra interrupts a sign-in that has not finished (the 'Stay
 * signed in?' prompt, an MFA step, a consent) and then records the real sign-in separately: staging shows one
 * 'Stay signed in?' sign-in as two `UserLoggedIn` records five seconds apart, which graded by location alone were
 * both a successful login from the same country (KRAK-296).
 *
 * `ExtendedProperties.ResultStatusDetail` is deliberately NOT used. 'Redirect' looks like an interrupt, but of the
 * 1,937 `UserLoggedIn` records in staging (September to October 2026) 976 say 'Redirect', and 840 of those are an
 * OAuth2:Authorize redirect, which is how a normal sign-in hands its code back: it would hide real sign-ins. Nor is
 * `ExtendedProperties.KeepMeSignedIn`, though it is on the record that precedes a 'Kmsi:kmsi' request (31 of each in
 * staging): it is undocumented, and a grade that hides a Danger should rest on documented fields.
 */
const isSignInInterrupted = (log: any): boolean => {
    if (isRealLogonError(log.LogonError)) {
        return true;
    }

    const errorNumber = String(log.ErrorNumber ?? log.ErrorCode ?? '').trim();

    return errorNumber !== '' && errorNumber !== '0';
};

/** How the interrupts seen in practice read in an alert. Anything else is shown as Entra names it. */
const INTERRUPT_REASONS = new Map<string, string>([
    ['KmsiInterrupt', 'keep me signed in prompt'],
    ['CmsiInterrupt', 'user confirmation prompt'],
    ['UserStrongAuthClientAuthNRequiredInterrupt', 'multi-factor authentication required'],
    ['UserStrongAuthEnrollmentRequiredInterrupt', 'multi-factor authentication registration required'],
]);

/** The reason an interrupted sign-in was interrupted, for the one-line description and the long one. */
const interruptReason = (fields: any): string => {
    const logonError: string | undefined = isRealLogonError(fields?.LogonError) ? fields.LogonError.trim() : undefined;

    return (logonError && INTERRUPT_REASONS.get(logonError)) || logonError || 'an additional sign-in step was required';
};

/**
 * Device Registration Service — the RESOURCE a device-code phish targets to enrol a device. On an STS
 * logon record the resource is `ObjectId` (verified on staging, 2026-10-20: an `az login` records
 * ObjectId = Azure Resource Manager, 797f4846-…), so this one is matched against `ObjectId`.
 */
const DEVICE_REGISTRATION_SERVICE = '01cb2876-7ebd-4aa4-9cc9-d28bd4d359a9';

/**
 * Well-known Microsoft first-party PUBLIC clients that have no browser of their own, so an
 * interactive browser sign-in requested by one of them is the victim-side leg of a device-code flow.
 * Attackers favour these precisely because they are pre-consented in every tenant, so nothing
 * has to be registered in the victim's directory (the Santini incident used the Authentication
 * Broker). A client is the REQUESTING application, which the Management Activity API's STS logon
 * schema carries in `ApplicationId` ("the application that is requesting the login"), never in
 * `ObjectId`. Lower-cased for comparison. The raw event store does not persist `ApplicationId`,
 * so this match can only happen here, at transform time, on the full record.
 *
 * Split by who legitimately runs them (OS-468, 2026-10-23). The ADMIN TOOLS are command-line and
 * developer clients an ordinary user never runs, so a sign-in through one earns a `warning` even
 * from an approved location. The BROKER clients are what Windows and Office themselves sign in
 * through: in the rule's first three days in production every sampled approved-location alert
 * (1,199 of 1,199) was routine broker or Office traffic to first-party resources, with no browser
 * user agent, so from an approved location those grade by location alone, like any other sign-in.
 * The Device Registration Service resource goes the same way: device enrolment is what it is for.
 * From an unapproved location all of them are still `danger`.
 */
const ADMIN_TOOL_CLIENT_APPS = new Set<string>([
    '04b07795-8ddb-461a-bbee-02f9e1bf7b46', // Microsoft Azure CLI
    '1950a258-227b-4e31-a9cf-717495945fc2', // Microsoft Azure PowerShell
    '14d82eec-204b-4c2f-b7e8-296a70dab67e', // Microsoft Graph PowerShell
    'aebc6443-996d-45c2-90f0-388ff96faa56', // Visual Studio Code
]);

/** The clients Windows and Office sign in through on every device; see {@link ADMIN_TOOL_CLIENT_APPS}. */
const BROKER_CLIENT_APPS = new Set<string>([
    '29d9ed98-a469-4536-ade2-f981bc1d605e', // Microsoft Authentication Broker
    'd3590ed6-52b3-4102-aeff-aad2292ab01c', // Microsoft Office
]);

const lowerCaseGuid = (value: unknown): string | null => (typeof value === 'string' ? value.toLowerCase() : null);

const UserLoggedIn: EventMeta = {
    title: 'User Login',
    default: {
        description: 'User Login',
        longDescription: 'A user login event occured.',
        severity: 'info',
    },
    fieldsExtractor: (log: any) => {
        const client = lowerCaseGuid(log.ApplicationId);
        const resource = lowerCaseGuid(log.ObjectId);
        const isAdminTool = client !== null && ADMIN_TOOL_CLIENT_APPS.has(client);
        const isNative =
            isAdminTool ||
            (client !== null && BROKER_CLIENT_APPS.has(client)) ||
            resource === DEVICE_REGISTRATION_SERVICE;
        // Sparse on purpose: only a sign-in requested by a native-only client (or enrolling a device)
        // carries the flags, so the ordinary sign-in keeps exactly the fields it has always had.
        return {
            SignInInterrupted: isSignInInterrupted(log),
            ...(isNative ? { native_client_app: true } : {}),
            ...(isAdminTool ? { admin_tool_client: true } : {}),
        };
    },
    descriptionExtractor: (log: any) => {
        // Only the sign-ins that carry an error pay for a description: a completed one has nothing to add.
        return isRealLogonError(log.LogonError) ? { LogonError: log.LogonError.trim() } : null;
    },
    classifications: [
        // interrupted sign-in: not a completed sign-in, so not graded by where it came from. The completed
        // sign-in is a record of its own and is graded on its own. First, so it takes precedence over the
        // location ladder below.
        {
            severity: 'info',
            description: (fields: any) => `A sign-in was interrupted (${interruptReason(fields)})`,
            longDescription: (fields: any) =>
                `Microsoft Entra interrupted this sign-in to ask for an additional step (${interruptReason(fields)}) before completing it. An interrupted sign-in is not a completed sign-in, so it is not graded by where it came from. If the user completes the sign-in, that is recorded as a separate event and graded on its own. No action is needed for this event.`,
            fields: {
                SignInInterrupted: [true],
            },
        },
        // DEVICE-CODE PHISHING (OS-437 step 1): the victim-side leg. An interactive browser
        // sign-in naming a native-only client app (Authentication Broker, Azure CLI, the Device
        // Registration Service) is what a device-code phish produces when the user approves the
        // attacker's code on the genuine Microsoft page. Sits right after the interrupted-sign-in
        // rule (an interrupted sign-in is not a completed one, KRAK-296) and ahead of the location
        // ladder, so it wins precedence over the generic geo classifications below, which would
        // otherwise claim the event with weaker wording.
        {
            severity: 'danger',
            description: `A sign-in to a native-only application came from an unapproved location`,
            longDescription:
                'A user interactively signed in to a native-only client application — for example the Microsoft Authentication Broker, Azure CLI, or the Device Registration Service — from a location that is not on your allowed list. This is the victim-side signature of device-code phishing, where a user is tricked into approving an attacker-initiated sign-in on the genuine Microsoft page, after which the tokens go to the attacker. Review the account and revoke its sessions if this sign-in was not expected.',
            fields: { native_client_app: [true], SignInInterrupted: [false] },
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isUserMemberOfOrganisation: true,
                isMicrosoftActivity: false,
            },
        },
        // successful login: fraudulent IP. user in org. country not allowed. ip not allowed.
        {
            severity: 'critical',
            description: `A suspicious IP performed a successful login`,
            longDescription:
                'A user successfully logged in from a suspicious IP address that is not in your allowed list. This activity may indicate an attempt to access the account without authorization.',
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: true,
            },
        },
        // successful login: fraudulent IP. user not in org. country not allowed. ip not allowed.
        {
            severity: 'info',
            description: `A suspicious IP used a non-org account to successfully log in`,
            longDescription:
                'A successful login occurred with an account that does not belong to your organisation. The action originated from a suspicious IP that is not on your allowed list. While no direct impact to your environment is indicated, this may reflect reconnaissance or background activity worth monitoring. No immediate response is required unless further unusual behaviour is observed.',
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: false,
            },
        },
        // successful login: country not allowed. user in org. region not allowed. ip not allowed.
        {
            severity: 'danger',
            description: `A login from an unapproved country was successful`,
            longDescription:
                'A user successfully logged in from an IP address located in a country that is not in your allowed list. This activity may indicate an attempt to access the account from an unauthorized location.',
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // successful login: country not allowed. user not in org. region not allowed. ip not allowed.
        {
            severity: 'info',
            description: `An unapproved country used a non-org account to successfully log in`,
            longDescription:
                'A successful login occurred with an account that does not belong to your organisation. The action originated from an unapproved country that is not in your allowed list. This event is informational and does not necessarily indicate malicious intent, but it may be useful for monitoring regional access patterns. Review only if unexpected.',
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // successful login: fraudulent IP. user in org. country not allowed. ip allowed.
        {
            severity: 'warning',
            description: `A suspicious IP within your allowed IP list successfully logged in`,
            longDescription:
                'A successful login was observed. The action originated from a suspicious IP, even though it is included in your allowed IP list. This may represent background scanning or activity from a flagged address that coincides with your approved network. No immediate response is required, but keep in mind this may warrant closer monitoring if similar events recur.',
            predicates: {
                isAllowedIP: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: true,
            },
        },
        // successful login: fraudulent IP. user not in org. country not allowed. ip allowed.
        {
            severity: 'info',
            description: `A suspicious IP within your allowed IP list used a non-org account to successfully log in`,
            longDescription:
                'A successful login occurred with an account that does not belong to your organisation. The action originated from a suspicious IP, even though it is part of your allowed IP list. While this may not indicate direct malicious activity, it is unusual and worth monitoring. Review only if unexpected, or if similar logins from flagged IPs continue.',
            predicates: {
                isAllowedIP: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: false,
            },
        },
        // successful login: fraudulent IP. user in org. in allowed region.
        {
            severity: 'warning',
            description: `A suspicious IP within your allowed region successfully logged in`,
            longDescription:
                'A successful login was observed. The action originated from a suspicious IP that falls inside your allowed region. This may indicate background or opportunistic activity from an address already present in an approved geography. While not immediately critical, it is worth noting and monitoring, especially if similar logins recur from flagged IPs.',
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: true,
            },
        },
        // successful login: fraudulent IP. user not in org. in allowed region.
        {
            severity: 'info',
            description: `A suspicious IP within your allowed region used a non-org account to successfully log in`,
            longDescription:
                'A successful login occurred with an account that does not belong to your organisation. The action originated from a suspicious IP located inside your allowed region. While this activity may not indicate direct malicious intent, it represents an anomaly that could be useful for monitoring. No immediate response is required, but keep an eye out for repeated or related logins from suspicious IPs.',
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: false,
            },
        },
        // successful login: fraudulent IP. user in org. in allowed ASN
        {
            severity: 'warning',
            description: `A suspicious IP within your allowed ASN list successfully logged in`,
            longDescription:
                'A successful login was observed. The action originated from a suspicious IP that belongs to an ASN you allow. This means the source is tied to a network normally approved in your policies, but the specific IP has been flagged as suspicious. While not directly harmful on its own, this represents an anomaly and should be monitored. If similar logins repeat from suspicious IPs in trusted ASNs, further review may be warranted.',
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: true,
            },
        },
        // successful login: fraudulent IP. user not in org. in allowed ASN
        {
            severity: 'info',
            description: `A suspicious IP within your allowed ASN list used a non-org account to successfully log in`,
            longDescription:
                'A successful login occurred with an account that does not belong to your organisation. The action originated from a suspicious IP within your allowed ASN list. This means the IP address was flagged as suspicious, even though it belongs to a network you normally permit. While not critical on its own, this is an anomaly worth recording. Monitoring for repeated activity of this type can help identify potential misuse hidden within trusted network ranges.',
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: false,
            },
        },
        // successful login: fraudulent IP. user in org. in allowed countries
        {
            severity: 'warning',
            description: `A suspicious IP within your allowed countries successfully logged in`,
            longDescription:
                'A successful login was observed. The action originated from a suspicious IP that falls inside your allowed country list. While the geography aligns with your policies, the specific IP address has been flagged as suspicious. This event is informational and does not require immediate response, but it is useful to monitor in case similar logins repeat from flagged IPs within trusted regions.',
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: true,
            },
        },
        // successful login: fraudulent IP. user not in org. in allowed countries
        {
            severity: 'info',
            description: `A suspicious IP within your allowed countries used a non-org account to successfully log in`,
            longDescription:
                'A successful login occurred with an account that does not belong to your organisation. The action originated from a suspicious IP that is in your allowed country list. While the geographic origin matches your policy, the specific IP is flagged as suspicious. This event is informational and not inherently harmful, but it provides context that may be useful for monitoring. No immediate action is needed unless related anomalies appear.',
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: false,
            },
        },
        // Same device-code signature from an approved location, narrowed to the admin tools (OS-468):
        // a user who ran Azure CLI or PowerShell deserves a question, while the Authentication Broker
        // and Office are routine and fall through to the ordinary successful-login grade below. Sits
        // here, after the geo classifications, so an unapproved-location login is still graded on its
        // location first.
        {
            severity: 'warning',
            description: `A sign-in through an administrative command-line tool was recorded`,
            longDescription:
                'A user signed in through a command-line or developer client — Azure CLI, Azure PowerShell, Microsoft Graph PowerShell or Visual Studio Code. These are the clients device-code phishing most often borrows, and an ordinary user rarely has reason to run them, so confirm with the user that this was them. If it was not, revoke the sessions for the account and check the Entra sign-in logs for device-code authentication.',
            fields: { admin_tool_client: [true], SignInInterrupted: [false] },
            predicates: {
                isUserMemberOfOrganisation: true,
            },
        },
        // successful login
        {
            severity: 'info',
            description: `A successful login was recorded`,
            longDescription:
                'A successful login was observed. This event is informational and records normal authentication activity. No unusual conditions were detected, and no action is required unless this login is unexpected.',
        },
    ],
};

const UserLoginFailed: EventMeta = {
    title: 'User Login Failed',
    default: {
        description: 'User Login Failed',
        longDescription: 'A failed login event occured.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => {
        return {
            LogonError: log.LogonError ? log.LogonError : null,
        };
    },
    classifications: [
        // failed login: user in org. country not allowed. not allowed Region. not allowed IP.
        {
            severity: 'warning',
            description: `An unapproved country attempted a failed login`,
            longDescription:
                'A failed login attempt was recorded. The action originated from an unapproved country that is not in your allowed list. This event is informational and typically indicates an unsuccessful authentication attempt. No direct risk is implied, but the geographic mismatch may be useful for monitoring. Review only if there are repeated failed logins or other related anomalies.',
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isUserMemberOfOrganisation: true,
            },
        },
        // failed login: user not in org. country not allowed. not allowed Region. not allowed IP.
        {
            severity: 'info',
            description: `An unapproved country used a non-org account to attempt a failed login`,
            longDescription:
                'A failed login attempt was recorded with an account that does not belong to your organisation. The action originated from an unapproved country that is not in your allowed list. This event is informational — failed logins like this are common and often harmless, but the geographic mismatch may provide useful context. No immediate response is required unless repeated failures or related anomalies are observed.',
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // failed login: user not in org. fraudulent IP. country not allowed. not allowed IP.
        {
            severity: 'info',
            description: `A suspicious IP used a non-org account to attempt a failed login`,
            longDescription:
                'A failed login attempt was recorded with an account that does not belong to your organisation. The action originated from a suspicious IP that is not on your allowed list. This event is informational — failed logins like this are common, but the involvement of a suspicious IP may provide useful context for monitoring. No immediate response is required unless there are repeated attempts or related anomalies.',
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: false,
            },
        },
        // failed login: user not in org. fraudulent IP. country not allowed. allowed IP.
        {
            severity: 'info',
            description: `A suspicious IP within your allowed IP list used a non-org account to attempt a failed login`,
            longDescription:
                'A failed login attempt was recorded with an account that does not belong to your organisation. The action originated from a suspicious IP included in your allowed IP list. While the login attempt was unsuccessful and therefore not a direct threat, the presence of a suspicious IP in an allowed range is noteworthy. This event is informational and requires no immediate action, but monitoring for repeated attempts from suspicious IPs inside trusted lists may be useful.',
            predicates: {
                isAllowedIP: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: false,
            },
        },
        {
            severity: 'info',
            description: `A suspicious IP within your allowed region used a non-org account to attempt a failed login`,
            longDescription:
                'A failed login attempt was recorded with an account that does not belong to your organisation. The action originated from a suspicious IP in one of your allowed countries. Since the login was unsuccessful, there is no direct impact. This event is informational and primarily useful for monitoring. No immediate action is required unless repeated attempts or related anomalies are observed.',
            predicates: {
                isAllowedRegion: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: false,
            },
        },
        // failed login: user not in org. fraudulent IP. in allowed countries
        {
            severity: 'info',
            description: `A suspicious IP within your allowed countries used a non-org account to attempt a failed login`,
            longDescription:
                'A failed login attempt was recorded with an account that does not belong to your organisation. The action originated from a suspicious IP in one of your allowed countries. Since the login was unsuccessful, there is no direct impact. This event is informational and primarily useful for monitoring. No immediate action is required unless repeated attempts or related anomalies are observed.',
            predicates: {
                isAllowedCountry: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: false,
            },
        },
        // failed login: user not in org. fraudulent IP. in allowed asn
        {
            severity: 'info',
            description: `A suspicious IP within your allowed ASN list used a non-org account to attempt a failed login`,
            longDescription:
                'A failed login attempt was recorded with an account that does not belong to your organisation. The action originated from a suspicious IP included in your allowed ASN list. Since the attempt was unsuccessful, this event is informational and does not pose direct impact. However, the presence of a suspicious IP within an approved network range is noteworthy. Monitoring for repeated failed logins from suspicious IPs in trusted ASNs can help identify potential misuse or reconnaissance activity.',
            predicates: {
                isAllowedASN: true,
                isFraudulentIP: true,
                isUserMemberOfOrganisation: false,
            },
        },
        // failed login: user not in org. country not allowed. not allowed IP
        {
            severity: 'info',
            description: `An unapproved country used a non-org account to attempt a failed login`,
            longDescription:
                'A failed login attempt was recorded with an account that does not belong to your organisation. The action originated from an unapproved country that is not in your allowed list. Since the attempt was unsuccessful, this event is informational and poses no immediate threat. However, geographic mismatches like this may be worth noting, especially if they occur repeatedly or coincide with other anomalies.',
            predicates: {
                isAllowedASN: false,
                isAllowedIP: false,
                isAllowedRegion: false,
                isAllowedCountry: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // failed login: user not in org. not allowed countries. allowed IP.
        {
            severity: 'info',
            description: `An IP in your allowed list used a non-org account to attempt a failed login`,
            longDescription:
                'A failed login attempt was recorded with an account that does not belong to your organisation. The action originated from an IP in your allowed list. Because the attempt was unsuccessful and the source IP is permitted, this event is informational only. No immediate response is required unless repeated failures or related anomalies appear.',
            predicates: {
                isAllowedIP: true,
                isMicrosoftActivity: false,
                isFraudulentIP: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // failed login: user not in org. not allowed countries. allowed IP.
        {
            severity: 'info',
            description: `An IP in your allowed ASN list used a non-org account to attempt a failed login`,
            longDescription:
                'A failed login attempt was recorded with an account that does not belong to your organisation. The action originated from an IP in your allowed ASN list. Because the attempt was unsuccessful and the source network is permitted, this event is informational only. No action is required unless repeated failures or unusual patterns are observed from the same ASN.',
            predicates: {
                isAllowedASN: true,
                isMicrosoftActivity: false,
                isFraudulentIP: false,
                isUserMemberOfOrganisation: false,
            },
        },
        // failed login: user not in org.
        {
            severity: 'info',
            description: `A non-org account attempted a failed login`,
            longDescription:
                'A failed login attempt was recorded with an account that does not belong to your organisation. No unusual IP or geographic conditions were noted for this event. This event is informational only and generally indicates a routine unsuccessful authentication attempt. No immediate action is required unless repeated failures are observed from the same source or correlated anomalies appear.',
            predicates: {
                isUserMemberOfOrganisation: false,
            },
        },
        // failed login
        {
            severity: 'info',
            description: `A failed login attempt was recorded`,
            longDescription:
                'A failed login attempt was recorded. No additional details (such as account type, IP origin, or geographic context) were associated with this event. This is a standard informational entry, typically reflecting an unsuccessful authentication attempt. No immediate action is required unless you see repeated failures or patterns that suggest unusual activity.',
        },
    ],
};

const operations: { [key: string]: EventMeta<Fields> } = {
    UserLoggedIn: UserLoggedIn,
    UserLoginFailed: UserLoginFailed,
};

export default operations;
